<template>
  <section v-if="error || suggestions.length" class="lead-communication-suggestions">
    <header>
      <strong>Suggested conversations</strong>
      <span v-if="suggestions.length">{{ suggestions.length }}</span>
      <button
        v-else-if="error"
        class="lead-communication-suggestions__retry"
        type="button"
        :disabled="loading"
        @click="loadSuggestions"
      >
        {{ loading ? 'Loading...' : 'Retry' }}
      </button>
    </header>
    <div v-if="error && !suggestions.length" class="lead-communication-suggestions__state" role="alert">
      <p>Unable to load suggestions.</p>
    </div>
    <article
      v-for="suggestion in suggestions"
      :key="suggestion.id"
      class="lead-communication-suggestions__item"
    >
      <div class="lead-communication-suggestions__item-heading">
        <div>
          <span>{{ providerName(suggestion.source) }}</span>
          <span>{{ valueName(suggestion.confidence) }}</span>
          <time
            v-tooltip="{ content: formatExactDate(suggestion.occurred_at), placement: 'top', offset: 8 }"
          >
            {{ formatRelativeDate(suggestion.occurred_at) }}
          </time>
        </div>
        <div class="lead-communication-suggestions__actions">
          <a
            v-if="trengoTicketUrl(suggestion)"
            class="lead-communication-suggestions__open-trengo"
            :href="trengoTicketUrl(suggestion)"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open Trengo
          </a>
          <button
            class="lead-communication-suggestions__accept"
            type="button"
            :disabled="Boolean(pendingIds[suggestion.id])"
            @click="resolve(suggestion, 'accept')"
          >
            {{ pendingIds[suggestion.id] ? 'Saving...' : 'Accept' }}
          </button>
          <button
            class="lead-communication-suggestions__reject"
            type="button"
            :disabled="Boolean(pendingIds[suggestion.id])"
            @click="resolve(suggestion, 'reject')"
          >
            Reject
          </button>
        </div>
      </div>
      <strong v-if="suggestion.subject">{{ suggestion.subject }}</strong>
      <p>{{ suggestion.text }}</p>
      <small v-if="suggestion.matched_identity">
        {{ suggestion.match_method }}: {{ suggestion.matched_identity.value }}
      </small>
      <p v-if="rowErrors[suggestion.id]" class="lead-communication-suggestions__error">
        {{ rowErrors[suggestion.id] }}
      </p>
    </article>
  </section>
</template>

<script>
import {
  getLeadCommunicationSuggestions,
  postLeadCommunicationSuggestionAction
} from '@/http'
import { formatExactDate, formatRelativeDate } from '@/views/crm/task-tracker/leadFlowDate'
import {
  createLeadContext,
  isActiveLeadContext
} from '@/views/crm/task-tracker/leadFlowContext'

export default {
  name: 'LeadCommunicationSuggestions',
  props: {
    leadId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      suggestions: [],
      loading: false,
      error: null,
      pendingIds: {},
      rowErrors: {},
      requestGeneration: 0,
      contextGeneration: 0
    }
  },
  watch: {
    leadId: {
      immediate: true,
      handler() {
        this.reset()
        this.loadSuggestions()
      }
    }
  },
  beforeDestroy() {
    this.requestGeneration += 1
    this.contextGeneration += 1
  },
  methods: {
    formatExactDate,
    formatRelativeDate,
    async loadSuggestions() {
      const leadId = this.leadId
      const generation = this.requestGeneration + 1
      this.requestGeneration = generation
      this.loading = true
      this.error = null
      try {
        const { data } = await getLeadCommunicationSuggestions(leadId)
        if (generation !== this.requestGeneration || leadId !== this.leadId) return
        const suggestions = Array.isArray(data) ? data : (data.results || [])
        this.suggestions = suggestions
        return {
          suggestions,
          error: null
        }
      } catch (error) {
        if (generation === this.requestGeneration && leadId === this.leadId) {
          this.error = error
        }
        return {
          suggestions: this.suggestions,
          error
        }
      } finally {
        if (generation === this.requestGeneration && leadId === this.leadId) {
          this.loading = false
        }
      }
    },
    async resolve(suggestion, action) {
      if (this.pendingIds[suggestion.id]) return
      const context = createLeadContext(this.leadId, this.contextGeneration)
      if (!context.leadId) return
      this.$set(this.pendingIds, suggestion.id, true)
      this.$delete(this.rowErrors, suggestion.id)
      this.$emit('pending-change', true)
      try {
        const { data } = await postLeadCommunicationSuggestionAction(
          context.leadId,
          suggestion.id,
          action
        )
        if (!isActiveLeadContext(context, this.leadId, this.contextGeneration)) return
        this.suggestions = this.suggestions.filter(item => item.id !== suggestion.id)
        if (action === 'accept') this.$emit('accepted', data || {})
        else this.$emit('rejected', suggestion.id)
        this.$notify({
          type: 'success',
          title: action === 'accept' ? 'Conversation linked' : 'Suggestion rejected'
        })
      } catch (error) {
        if (!isActiveLeadContext(context, this.leadId, this.contextGeneration)) return
        const data = error.response && error.response.data
        this.$set(
          this.rowErrors,
          suggestion.id,
          data && data.message ? data.message : 'Unable to resolve this suggestion.'
        )
      } finally {
        if (isActiveLeadContext(context, this.leadId, this.contextGeneration)) {
          this.$delete(this.pendingIds, suggestion.id)
          this.$emit('pending-change', Object.keys(this.pendingIds).length > 0)
        }
      }
    },
    providerName(source) {
      return source === 'trengo' ? 'Trengo' : 'Gmail'
    },
    valueName(value) {
      return value && (value.name || value.code) ? (value.name || value.code) : ''
    },
    trengoTicketUrl(suggestion) {
      if (!suggestion || suggestion.source !== 'trengo') return ''
      const ticketId = String(suggestion.external_resource_id || '').trim()
      return ticketId ? `https://app.trengo.com/tickets/${encodeURIComponent(ticketId)}` : ''
    },
    reset() {
      this.requestGeneration += 1
      this.contextGeneration += 1
      this.suggestions = []
      this.loading = false
      this.error = null
      this.pendingIds = {}
      this.rowErrors = {}
    }
  }
}
</script>

<style scoped>
.lead-communication-suggestions {
  display: grid;
  gap: 8px;
  padding: 10px;
  border: 1px solid #ead39b;
  border-radius: 6px;
  background: #fff9e9;
}

.lead-communication-suggestions > header,
.lead-communication-suggestions__item-heading,
.lead-communication-suggestions__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.lead-communication-suggestions > header strong {
  color: #7a622c;
  font-size: 9px;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.lead-communication-suggestions > header span {
  color: #9a823f;
  font-size: 9px;
}

.lead-communication-suggestions__item {
  padding: 9px;
  border: 1px solid #efdeb2;
  border-radius: 5px;
  background: rgba(255, 255, 255, .76);
}

.lead-communication-suggestions__item-heading > div {
  display: flex;
  gap: 5px;
}

.lead-communication-suggestions__item-heading span {
  padding: 2px 5px;
  border-radius: 8px;
  background: #f3e7c8;
  color: #765f2e;
  font-size: 8px;
  font-weight: 700;
}

.lead-communication-suggestions time {
  color: #9a8d69;
  font-size: 8px;
}

.lead-communication-suggestions__item > strong {
  display: block;
  margin-top: 7px;
  color: #4d4d42;
  font-size: 10px;
}

.lead-communication-suggestions__item > p {
  margin: 5px 0 0;
  color: #5d5a4b;
  font-size: 10px;
  line-height: 14px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.lead-communication-suggestions__item > small {
  display: block;
  margin-top: 5px;
  color: #8b8163;
  font-size: 12px;
}

.lead-communication-suggestions__actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.lead-communication-suggestions__open-trengo,
.lead-communication-suggestions__actions button {
  min-height: 27px;
  padding: 0 9px;
  border: 1px solid #8eb951;
  border-radius: 4px;
  background: #fff;
  color: #588620;
  font-size: 9px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color .15s ease;
}

.lead-communication-suggestions__open-trengo {
  display: inline-flex;
  align-items: center;
  border-color: #8daed6;
  color: #2f70b7;
  text-decoration: none;
}

.lead-communication-suggestions__reject {
  border-color: #d8a7a1;
  color: #ad564e;
}

.lead-communication-suggestions__open-trengo:hover,
.lead-communication-suggestions__open-trengo:focus-visible {
  background: rgba(47, 112, 183, .1);
}

.lead-communication-suggestions__accept:hover:not(:disabled) {
  background: rgba(142, 185, 81, .12);
}

.lead-communication-suggestions__reject:hover:not(:disabled) {
  background: rgba(216, 167, 161, .14);
}

.lead-communication-suggestions__error {
  color: #b85049 !important;
}

.lead-communication-suggestions__state p {
  margin: 0;
  color: #806c3c;
  font-size: 10px;
}

.lead-communication-suggestions__retry {
  min-height: 22px;
  padding: 0 8px;
  border: 1px solid #c9d6ac;
  border-radius: 4px;
  background: rgba(255, 255, 255, .72);
  color: #6c8e32;
  font-size: 9px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}

.lead-communication-suggestions__retry:hover:not(:disabled),
.lead-communication-suggestions__retry:focus-visible:not(:disabled) {
  border-color: #aac27b;
  background: rgba(142, 185, 81, .14);
}

.lead-communication-suggestions__retry:disabled {
  cursor: wait;
  opacity: .65;
}

</style>
